import {
  Card,
  CardBody,
  CardProps,
  Flex,
  LinkBox,
  LinkOverlay,
  Image,
  Text,
  Divider,
  IconButton,
  HStack,
  Tag,
} from "@chakra-ui/react";
import ButtonWithConfirm from "components/Button/ButtonWithConfirm";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { useDeleteIntegration, useToggleActiveIntegration } from "features/Integrations/hooks";
import { PropsWithChildren } from "react";
import { generatePath, Link } from "react-router";
import { timeAtDayMonth } from "helpers/time";
import { IoMdPause, IoMdPlay } from "react-icons/io";
import { DeleteIcon } from "@chakra-ui/icons";
import { greenStyles, orangeStyles } from "components/Tag/Status/styles";
import { Heading20 } from "components/Heading";
import { TertiaryButton } from "components/Button";
import SyncChannelsButton from "./SyncChannelsButton";

const CONFIRM_DELETE = {
  header: "Remove Integration",
  body: "Are you sure you'd like to remove the credentials for this integration?",
  confirmButtonText: "Yes, remove",
};

interface IntegrationCardProps extends CardProps, PropsWithChildren {
  id?: string;
  name: string;
  partner: string;
  description: string;
  logoUrl?: string;
  isActive?: boolean;
  isConnected?: boolean;
  includeDelete?: boolean;
  pauseable?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  onConnect?: () => void;
  connectButtonText?: string;
  messageService?: {
    channelsLastSyncedAt?: Date;
    usersLastSyncedAt?: Date;
    url?: string;
  };
}

export default function IntegrationCard({
  id,
  name,
  partner,
  description,
  logoUrl,
  isActive = undefined,
  isConnected = false,
  includeDelete = false,
  pauseable = false,
  createdAt,
  updatedAt,
  onConnect,
  connectButtonText = "Connect",
  messageService,
  ...rest
}: IntegrationCardProps) {
  const { performRequest: handleDelete } = useDeleteIntegration(id || "");
  const { performRequest: toggleIsActive } = useToggleActiveIntegration(id || "", isActive || false);

  const isSlack = partner === "slack";
  const showSyncChannels = isSlack && isConnected;
  const showPauseButton = pauseable && !isSlack;
  const colorScheme = isActive ? greenStyles : orangeStyles;

  return (
    <LinkBox
      as={Card}
      minWidth="380px"
      maxWidth="380px"
      _hover={{ bg: "bg.tertiary" }}
      {...rest}
    >
      <CardBody height="100%" display="flex" flexDirection="column" p={6}>
        <Flex alignItems="center" justifyContent="space-between" mb={4}>
          <HStack spacing={3} flex={1}>
            {logoUrl && (
              <Image
                src={logoUrl}
                alt={partner}
                w="32px"
                h="32px"
                objectFit="contain"
                borderRadius="md"
              />
            )}
            <Heading20>
              {isConnected && id ? (
                <LinkOverlay as={Link} to={generatePath(PEOPLE_TEAM_ROUTES.company.integrations.show, { id })}>
                  {name}
                </LinkOverlay>
              ) : (
                name
              )}
            </Heading20>
          </HStack>
          {showSyncChannels && (
            <SyncChannelsButton />
          )}
        </Flex>

        <Flex direction="column" flex={1} mb={4}>
          {isConnected ? (
            <>
              {createdAt && (
                <Flex justifyContent="space-between" alignItems="center" mb={1}>
                  <Text fontSize="xs" color="fg.muted">
                    Added:
                  </Text>
                  <Text fontSize="sm" color="fg.default" pr={32}>
                    {timeAtDayMonth(createdAt)}
                  </Text>
                </Flex>
              )}
              {updatedAt && (
                <Flex justifyContent="space-between" alignItems="center">
                  <Text fontSize="xs" color="fg.muted">
                    Last updated:
                  </Text>
                  <Text fontSize="sm" color="fg.default" pr={32}>
                    {timeAtDayMonth(updatedAt)}
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            <Text fontSize="sm" color="fg.muted">
              {description}
            </Text>
          )}
        </Flex>

        <Flex direction="column">
          <Divider mb={4} />

          {isConnected ? (
            <Flex alignItems="center" justifyContent="space-between">
              <Tag
                size="lg"
                fontSize="sm"
                {...colorScheme}
              >
                {isActive ? "Active" : "Paused"}
              </Tag>

              <HStack spacing={1}>
                {showPauseButton && (
                  <IconButton
                    aria-label={isActive ? "Pause Integration" : "Resume Integration"}
                    icon={isActive ? <IoMdPause /> : <IoMdPlay />}
                    size="sm"
                    variant="ghost"
                    onClick={toggleIsActive}
                  />
                )}

                {includeDelete && (
                  <ButtonWithConfirm {...CONFIRM_DELETE} intent="warning" handleConfirm={handleDelete}>
                    <IconButton
                      aria-label="Delete Integration"
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                    />
                  </ButtonWithConfirm>
                )}
              </HStack>
            </Flex>
          ) : (
            <TertiaryButton
              onClick={onConnect}
              size="sm"
              height="100%"
              px={6}
              py={1}
              alignSelf="flex-start"
            >
              {connectButtonText}
            </TertiaryButton>
          )}
        </Flex>
      </CardBody>
    </LinkBox>
  );
}
